import { Button, DateField, useAlert } from "matsuri-ui"
import { css } from "@emotion/react"
import { useState } from "react"
import dayjs from "dayjs"
import { TimeField } from "./TimeField"
import { genFieldNames } from "@/utils/fieldTypes"
import { Shift, getShiftCurrentTime } from "@/model/shift"
import { useTranslation } from "@/hooks/useTranslation"

const { fieldNames, getFormData } = genFieldNames([
    "date",
    "startTime",
    "endTime"
])

// date, startTime, endTimeフィールドが揃っている必要があることに注意
export const useShiftForm = () => {
    const { throwAlert } = useAlert()
    const { t } = useTranslation()
    const validate = (date: string, startTime: string, endTime: string) => {
        const start = dayjs(`${date} ${startTime}`).unix()
        const end = dayjs(`${date} ${endTime}`).unix()

        if (start >= end) {
            throwAlert(
                new Error(t("終了時刻は開始時刻より後にしてください。")),
                {
                    errorMessage: {
                        happend: t("終了時刻は開始時刻より後にしてください。")
                    }
                }
            )

            return
        }

        return {
            start,
            end
        }
    }

    return {
        fieldNames,
        handleSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            const input = getFormData(event.currentTarget)

            return validate(input.date, input.startTime, input.endTime)
        },
        validate
    }
}

export const ShiftForm = ({
    creates,
    data,
    onSubmit,
    onCancel,
    onDelete,
    onUnsubmit,
    initialDate,
    initialStartTime,
    initialEndTime
}: {
    creates?: boolean
    data?: Shift
    onSubmit: (input: { start: number; end: number }) => void
    onCancel?: () => void
    onDelete?: () => void
    onUnsubmit?: () => void
    // initial~はcreate時のみ有効なprops
    // ベタに書いているのでかなりダサいが、create時には「dateのみ入れてstart,endはundefinedのままにしたい」などのケースがあるのでしょうがないということにする
    initialDate?: string
    initialStartTime?: number
    initialEndTime?: number
}) => {
    const { throwAlert } = useAlert()
    const { t } = useTranslation()
    const [startTime, setStartTime] = useState<string | undefined>(
        data
            ? dayjs.unix(getShiftCurrentTime(data).start).format("HH:mm")
            : initialStartTime
              ? dayjs.unix(initialStartTime).format("HH:mm")
              : undefined
    )
    const [endTime, setEndTime] = useState<string | undefined>(
        data
            ? dayjs.unix(data.cleanerEnd).format("HH:mm")
            : initialEndTime
              ? dayjs.unix(initialEndTime).format("HH:mm")
              : undefined
    )

    return (
        <form
            onSubmit={async event => {
                event.preventDefault()

                const formData = new FormData(event.currentTarget)
                const input = Object.fromEntries(formData.entries())

                const start = dayjs(`${input.date} ${input.startTime}`).unix()
                const end = dayjs(`${input.date} ${input.endTime}`).unix()

                if (start >= end) {
                    throwAlert(
                        new Error(
                            t("終了時刻は開始時刻より後にしてください。")
                        ),
                        {
                            errorMessage: {
                                happend: t(
                                    "終了時刻は開始時刻より後にしてください。"
                                )
                            }
                        }
                    )

                    return
                }

                onSubmit({
                    start,
                    end
                })
            }}
            css={css`
                display: grid;
                gap: 16px;
            `}
        >
            <div
                css={css`
                    display: grid;
                    gap: 8px;
                `}
            >
                <DateField
                    name="date"
                    label={t("日付")}
                    defaultValue={
                        data
                            ? dayjs
                                  .unix(getShiftCurrentTime(data).start)
                                  .format("YYYY-MM-DD")
                            : (initialDate ?? dayjs().format("YYYY-MM-DD"))
                    }
                    // 日付を変えることはあまり想定しないでいいと思うので、編集時にはreadOnlyにしてしまう
                    readOnly={!creates}
                />
                <TimeField
                    label={t("開始時間")}
                    name="startTime"
                    value={startTime}
                    onChange={value => {
                        if (value) {
                            setStartTime(value)
                        }
                    }}
                />
                <TimeField
                    label={t("終了時間")}
                    name="endTime"
                    min={startTime}
                    value={endTime}
                    onChange={value => {
                        if (value) {
                            setEndTime(value)
                        }
                    }}
                />
            </div>

            <div
                css={css`
                    display: flex;
                    gap: 8px;
                    align-items: flex-end;
                    justify-content: space-between;
                `}
            >
                {onUnsubmit && (
                    <div>
                        <Button
                            type="button"
                            color="error"
                            onClick={onUnsubmit}
                        >
                            {t("下書きに戻す")}
                        </Button>
                    </div>
                )}
                {onDelete && (
                    <div>
                        <Button type="button" color="error" onClick={onDelete}>
                            {t("削除")}
                        </Button>
                    </div>
                )}

                {onCancel && (
                    <Button type="button" onClick={onCancel}>
                        {t("キャンセル")}
                    </Button>
                )}
                <div>
                    <Button variant="filled" color="primary">
                        {t("送信")}
                    </Button>
                </div>
            </div>
        </form>
    )
}
